import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import {
  AreaChart,
  BarChart,
  Area,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Line,
  ComposedChart,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const chartMargin = {
  top: 20,
  right: 30,
  left: 20,
  bottom: 5,
};

const Chart = ({ risksThroughTime, chartType, viewRiskPage }) => {
  const { t } = useTranslation();

  const dataKeys = [
    { key: 'low', color: 'var(--green-risk-score)' },
    { key: 'medium', color: 'var(--yellow-risk-score)' },
    { key: 'high', color: 'var(--red-risk-score)' },
    { key: 'avgRiskScore', color: 'var(--primary-color)'},
    { key: 'totalRiskScore', color: 'var(--primary-color)'}
  ];
  
  const [visibleKeys, setVisibleKeys] = useState(
    dataKeys.reduce((acc, { key }) => ({ ...acc, [key]: true }), {})
  );

  const toggleLegend = (key) => {
    // Handle different possible structures from Recharts legend click
    const keyName = key.name || key.dataKey || key.id || key;
    setVisibleKeys(prevVisibleKeys => ({
      ...prevVisibleKeys,
      [keyName]: !prevVisibleKeys[keyName],
    }));
  };

  const renderChart = (ChartComponent, ChartElement, showAverageScore) => {
    const isSingleRisk = chartType === 'SingleRisk';
    const isViewRiskPage = viewRiskPage === "true" || viewRiskPage === true;
    
    return (
      <ChartComponent
        data={risksThroughTime}
        margin={chartMargin}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis yAxisId="left" domain={[0, viewRiskPage ? 25 : 'dataMax']} />
        {showAverageScore && !isSingleRisk && !isViewRiskPage && (<YAxis yAxisId="right" orientation="right" domain={[0, 'dataMax']} />)}
        
        {/* Single Risk Score Line */}
        {isSingleRisk && (
          <Line
            yAxisId="left"
            key="totalRiskScore"
            name={t('risk_score')}
            type="monotone"
            dataKey="totalRiskScore"
            fill="var(--primary-color)"
            stroke="var(--primary-color)"
            strokeWidth={3}
            dot={{ fill: 'var(--primary-color)', strokeWidth: 2, r: 4 }}
            hide={visibleKeys['totalRiskScore'] !== true}
          />
        )}
        
        {/* Average Risk Score Line for multi-risk charts */}
        {showAverageScore && !isSingleRisk && !isViewRiskPage && (
          <Line
            yAxisId="right"
            key="avgRiskScore"
            name={t('average_risk_score')}
            type="monotone"
            dataKey="avgRiskScore"
            fill="var(--primary-color)"
            stroke="var(--primary-color)"
            stackId="1"
            hide={visibleKeys['avgRiskScore'] !== true}
          />
        )}
        
        <Tooltip 
          formatter={(value, name, props) => {
            if (isSingleRisk && name === t('risk_score')) {
              return [`${value}`, `${t('risk_score')}`];
            }
            if (isViewRiskPage) {
              // For risk levels in ViewRiskPage, show the score value or hide if 0
              return value > 0 ? [`${value}`, name] : null;
            }
            return [`${value} ${name === t('average_risk_score') ? '' : 'risks'}`, name];
          }}
          itemSorter={(item) => {
            // Sort tooltip items: low, medium, high, then avgRiskScore
            const order = { [t('low')]: 0, [t('medium')]: 1, [t('high')]: 2, [t('average_risk_score')]: 3 };
            return order[item.name] ?? 4;
          }}
        />
        
        <Legend 
          payload={dataKeys
            .filter(({ key }) => {
              if (isSingleRisk) return key === 'totalRiskScore';
              if (isViewRiskPage) return key !== 'avgRiskScore';
              return !(!showAverageScore && key === 'avgRiskScore') && key !== 'totalRiskScore';
            })
            .map(({ key, color }) => ({
              value: key === 'totalRiskScore' ? t('risk_score') : t(key),
              name: key,
              type: 'square',
              id: key,
              color: visibleKeys[key] ? color : "#8e8fa2"
            }))}  
          onClick={toggleLegend}
        />
        
        {/* Risk level areas for ViewRiskPage */}
        {isViewRiskPage && dataKeys
          .filter(({ key }) => key !== 'avgRiskScore' && key !== 'totalRiskScore')
          .map(({ key, color }) => (
          <Area
            yAxisId="left"
            key={key}
            name={t(key)}
            type="stepAfter"
            dataKey={key}
            fill={color}
            stroke={color}
            fillOpacity={0.6}
            strokeWidth={0}
            hide={visibleKeys[key] !== true}
          />
        ))}
        
        {/* Risk level areas/bars for multi-risk charts */}
        {!isSingleRisk && !isViewRiskPage && dataKeys
          .filter(({ key }) => key !== 'avgRiskScore' && key !== 'totalRiskScore')
          .map(({ key, color }) => (
          <ChartElement
            yAxisId="left"
            key={key}
            name={t(key)}
            type="monotone"
            dataKey={key}
            fill={color}
            stroke={color}
            hide={visibleKeys[key] !== true}
            stackId="1"
          />
        ))}
      </ChartComponent>
    );
  };

  const ChartElement = chartType === 'Bar' ? Bar : Area;

  // Pie chart specific rendering
  if (chartType === 'Pie') {
    // Transform data for pie chart - sum up all risk levels for each owner
    const pieData = risksThroughTime.map(item => {
      const total = (item.high || 0) + (item.medium || 0) + (item.low || 0);
      return {
        name: item.name,
        value: total,
        high: item.high || 0,
        medium: item.medium || 0,
        low: item.low || 0,
      };
    }).filter(item => item.value > 0); // Only show owners with risks

    const COLORS = ['#8b5cf6', '#f59e0b', '#ef4444', '#10b981', '#3b82f6', '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16', '#06b6d4', '#a855f7', '#eab308', '#f43f5e', '#22c55e', '#8b5cf6', '#fb923c', '#0ea5e9', '#d946ef', '#facc15'];

    return (
      <ResponsiveContainer width="100%" height="92%" initialDimension={{ width: 320, height: 200 }}>
        <PieChart>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
            outerRadius={120}
            fill="#8884d8"
            dataKey="value"
          >
            {pieData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip 
            formatter={(value, name, props) => [
              `${value} risks`,
              `${props.payload.name}`
            ]}
            labelFormatter={() => 'Owner'}
          />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="92%" initialDimension={{ width: 320, height: 200 }}>
      {renderChart(
        chartType === 'SingleRisk' ? LineChart : ComposedChart, 
        ChartElement, 
        chartType !== 'Bar' && !viewRiskPage && chartType !== 'SingleRisk'
      )}
    </ResponsiveContainer>
  );
};

export default Chart;